import * as Icon from "@iyk/icons"
import * as React from "react"
import { cx } from "./cva.ts"

const CollapsibleContext = React.createContext<Collapsible.Context | undefined>(undefined)

const Root = ({
  defaultOpen = false,
  open,
  onOpenChange,
  animationDuration = 300,
  children,
  className,
  ...props
}: Collapsible.RootProps) => {
  const id = React.useId()
  const [isOpen, setIsOpen] = React.useState(defaultOpen)
  const isControlled = open !== undefined

  const toggle = () => {
    if (!isControlled) {
      setIsOpen((prev) => !prev)
    }
    onOpenChange?.(isOpen)
  }

  const currentOpen = isControlled ? open : isOpen

  return (
    <CollapsibleContext.Provider
      value={{
        isOpen: currentOpen,
        toggle,
        animationDuration,
        id,
      }}
    >
      <div
        data-state={currentOpen ? "open" : "closed"}
        className={cx("w-full group", className)}
        {...props}
      >
        {children}
      </div>
    </CollapsibleContext.Provider>
  )
}

const useCollapsible = () => {
  const context = React.use(CollapsibleContext)
  if (!context) {
    throw new Error("useCollapsible must be used within a Collapsible.Root")
  }
  return context
}

const Trigger = ({ children, className, icon, ...props }: Collapsible.TriggerProps) => {
  const { isOpen, toggle, id } = useCollapsible()

  return (
    <button
      className={cx("w-full flex items-center", "disabled:pointer-events-none", className)}
      onClick={toggle}
      aria-expanded={isOpen}
      aria-controls={`${id}-content`}
      {...props}
    >
      {children}
      {icon ?? (
        <Icon.ChevronDown className="group-data-[state=open]:rotate-180 group-data-[state=closed]:rotate-0" />
      )}
    </button>
  )
}

const Content = ({ children, className, ...props }: Collapsible.ContentProps) => {
  const { isOpen, id, animationDuration } = useCollapsible()

  return (
    <div
      id={`${id}-content`}
      aria-hidden={!isOpen}
      className={cx(
        "grid grid-rows-[0fr]",
        "aria-[hidden=false]:grid-rows-[1fr]",
        "transition-[grid-template-rows] ease-in-out",
        className,
      )}
      style={{ transitionDuration: `${animationDuration}ms` }}
      {...props}
    >
      <div className="overflow-hidden">{children}</div>
    </div>
  )
}

export namespace Collapsible {
  export type RootProps = {
    defaultOpen?: boolean
    open?: boolean
    onOpenChange?: (open: boolean) => void
    animationDuration?: number
  } & React.ComponentProps<"div">
  export type Context = {
    id: string
    isOpen: boolean
    toggle: () => void
    animationDuration: number
  }
  export type TriggerProps = React.ComponentProps<"button"> & { icon?: React.ReactNode }
  export type ContentProps = React.ComponentProps<"div">
}

export const Collapsible = { Root, Trigger, Content, useCollapsible }
